import type { AppComboboxOption } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { AppBadge, AppCombobox } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { AlertCircle } from 'lucide-react'
import { useCallback, useMemo } from 'react'

type MemberOptionData = { employeeNumber: number | null }

const TBA_OPTION: AppComboboxOption = { value: 'tba', label: 'TBA/TBD' }

const getEmployeeNumber = (option: AppComboboxOption) => (option.data as MemberOptionData | undefined)?.employeeNumber

const renderMemberOption = (option: AppComboboxOption) => {
  if (option.value === TBA_OPTION.value) {
    return <span>{option.label}</span>
  }
  const employeeNumber = getEmployeeNumber(option)
  if (employeeNumber != null) {
    return (
      <div className='flex items-center gap-2'>
        <AppBadge outline color='blue' className='font-mono'>
          {employeeNumber}
        </AppBadge>
        <span>{option.label || '-'}</span>
      </div>
    )
  }
  if (option.label) {
    return <span>{option.label}</span>
  }
  return (
    <AppBadge color='amber' className='font-mono'>
      未割当
    </AppBadge>
  )
}

/** アイコン付きで出すメッセージの文字色。warning は注意（候補が0人など）、error は失敗 */
const ALERT_MESSAGE_COLORS = {
  warning: 'text-amber-600 dark:text-amber-400',
  error: 'text-destructive',
} as const

const searchConfig = {
  placeholder: '氏名または社員番号で検索...',
  filterFn: (option: AppComboboxOption, query: string) => {
    const q = query.toLowerCase()
    if (option.label.toLowerCase().includes(q)) return true
    const employeeNumber = getEmployeeNumber(option)
    return employeeNumber != null && String(employeeNumber).includes(q)
  },
} as const

type MemberComboboxProps = {
  value?: string
  onValueChange?: (value: string) => void
  options: AppComboboxOption[]
  label: string
  placeholder?: string
  disabled?: boolean
  /** 候補の取得中。選択を塞ぎ、読み込み中の表示にする */
  loading?: boolean
  clearable?: boolean
  /** コンボボックス下部に表示するメッセージ */
  message?: string
  /** コンボボックス下部のメッセージの表示種別（warning・error はアイコン付きで、error は赤字、デフォルト: default） */
  messageVariant?: 'default' | 'warning' | 'error'
  /** 候補が無いときに表示するメッセージ */
  emptyMessage?: string
  /** TBA/TBD選択肢を追加し、空値をTBAとして扱う（デフォルト: false） */
  allowTba?: boolean
}

export function MemberCombobox({
  value,
  onValueChange,
  options,
  label,
  placeholder = 'メンバーを選択',
  disabled,
  loading,
  clearable = true,
  message,
  messageVariant = 'default',
  emptyMessage = 'メンバーが見つかりません',
  allowTba = false,
}: MemberComboboxProps) {
  const resolvedOptions = useMemo(() => (allowTba ? [TBA_OPTION, ...options] : options), [allowTba, options])
  const resolvedValue = allowTba && value === '' ? TBA_OPTION.value : value
  const handleValueChange = useCallback(
    (v: string) => onValueChange?.(allowTba && v === TBA_OPTION.value ? '' : v),
    [onValueChange, allowTba],
  )
  return (
    <div>
      <AppCombobox
        value={resolvedValue}
        onValueChange={handleValueChange}
        options={resolvedOptions}
        placeholder={placeholder}
        disabled={disabled}
        loading={loading}
        clearable={clearable}
        field={{ label }}
        search={{ ...searchConfig, emptyMessage }}
        customization={{
          renderDisplayValue: renderMemberOption,
          renderOption: renderMemberOption,
          // kiban の既定幅は Tailwind v3 記法（w-[--radix-...]）で v4 では効かず、文言がはみ出すため入力欄の幅に合わせる
          contentClassName: 'w-(--radix-popover-trigger-width)',
        }}
      />
      {message &&
        (messageVariant === 'default' ? (
          <p className='mt-1 whitespace-pre-line text-xs text-muted-foreground'>{message}</p>
        ) : (
          <p
            className={`mt-1 flex items-start gap-1 whitespace-pre-line text-xs ${ALERT_MESSAGE_COLORS[messageVariant]}`}
          >
            <AlertCircle className='mt-px size-3.5 shrink-0' />
            {message}
          </p>
        ))}
    </div>
  )
}
